/**
 * ForecastEngine.calculateForecast (CONVENTIONS §5.5, TIMELINE R19–R23): a plan's cashflow and its
 * one timeline as seen on the clock's day. The line starts at now (the position at the end of
 * today) and each calendar month's bucket holds the position at the end of that month, so the
 * months since a check-in are carried, never skipped. Pure composition of the cashflow, debt,
 * investment, equity and safe-withdrawal modules. Each asset counts once (R1), sold assets (legacy
 * or unified) stop at their sale month and settle their linked debts (R2, R9), and each debt is
 * subtracted once (R3); nothing counts before its date and pots follow the ledger (R23). The
 * divergence it inherits (D5) is reproduced as-is.
 */
import { addDays, addMonths, type Day } from '../core/calendar.ts';
import { add, round2, sub, ZERO, type Decimal } from '../core/decimal.ts';
import { calculateCashflow, type CashflowForecast } from '../calculations/cashflow.ts';
import { debtFreeDate, projectDebts } from '../calculations/debt-projector.ts';
import { totalPropertyEquity } from '../calculations/equity.ts';
import { createInvestmentTransactionForecast, type InvestmentInterestForecast } from '../calculations/investment-projector.ts';
import { runPot } from '../calculations/pot.ts';
import { currentPosition, type CumulativeSeries, type Position } from '../calculations/metrics.ts';
import { safeWithdrawalMonthlyIncome } from '../calculations/safe-withdrawal.ts';
import { countsAsDebt } from '../calculations/net-worth.ts';
import { planSaleDates, saleSettlements, settleSoldAssetDebts } from '../calculations/sales.ts';
import { activeAssets, cashBaseline, investmentBaseline, propertyIds, totalDepreciatingAssetValue } from '../models/plan.ts';
import type {
  Bucket, CalcContext, Checkpoint, Id, Plan, Series, Transaction, TransactionType, Window,
} from '../types/domain.ts';
import { timeBuckets, windowFromPlanConfiguration } from './window.ts';

export interface ForecastOptions {
  /**
   * The latest check-in: now's cash (R17) and, unless `investmentCheckpoint` says otherwise, its
   * investments (R18) count from it. Nothing else is read from it: debt, property equity and asset
   * value come from the plan (R10).
   */
  readonly checkpoint?: Checkpoint | null;
  /** The check-in investments count from when it is not `checkpoint` (a later one's investments were an estimate, R25). */
  readonly investmentCheckpoint?: Checkpoint | null;
  /** Mortgages the user chose to show in the debt line instead of netting them in property equity (R3). */
  readonly mortgageIdsTreatedAsDebt?: Iterable<Id>;
}

export interface CumulativeForecast extends CumulativeSeries {
  /** Opportunity cost of each active debt and expense had it been invested; keyed by id. */
  readonly potential: ReadonlyMap<Id, InvestmentInterestForecast>;
  /** The day the debt line is first clear (R15): metrics and debt-free goals are dated by it. */
  readonly debtFreeDate: Day | null;
}

export interface ForecastResult {
  readonly window: Window;
  readonly cashflow: CashflowForecast;
  /** One bucket per calendar month from this one: the position at the end of the month (R22). */
  readonly cumulative: CumulativeForecast;
  /** The position at the end of the clock's day (R19), where the line starts (R20). */
  readonly now: Position;
  /** The line's values at the end of last month, as the map carries them back from now: where this month's bar starts (R22). */
  readonly opening: LineValues;
}

/** One point of the line, in the cumulative series' terms. */
export type LineValues = Readonly<Record<'cash' | 'investment' | 'equity' | 'debt' | 'netWorth', Decimal>>;

const ofType = (transactions: readonly Transaction[], type: TransactionType): readonly Transaction[] =>
  transactions.filter((t) => t.type === type);

// MARK: Series

const withAmounts = (buckets: Series, amounts: readonly Decimal[]): Series =>
  amounts.map((amount, i) => ({ start: buckets[i]!.start, end: buckets[i]!.end, amount }));

/** Running balance: previous + bucket. */
function accumulate(opening: Decimal, payments: Series): Series {
  let balance = opening;
  return payments.map((bucket) => {
    balance = add(balance, bucket.amount);
    return { ...bucket, amount: balance };
  });
}

/**
 * Every investment pot summed at the end of each of `days` (ascending), on the ledger's rules
 * (R12, R23: payments on the day they are made, month-end growth on the month before's balance,
 * withdrawals stopping at 0), items in plan order.
 */
function potTotals(investments: readonly Transaction[], incomes: readonly Transaction[], days: readonly Day[], fallbackReturnPercent: Decimal): readonly Decimal[] {
  const runs = investments.map((t) => runPot(t, incomes, fallbackReturnPercent, days).values);
  return days.map((_, i) => runs.reduce<Decimal>((total, values) => add(total, values[i]!), ZERO));
}

/**
 * Equity at the end of each bucket's last day (R22): Plan.totalPropertyEquity (not netting mortgages
 * treated as debt) plus Plan.totalDepreciatingAssetEquity, the gross value of the non-property assets
 * not yet sold (R1–R3, R10).
 */
function equitySeries(plan: Plan, buckets: Series, ctx: CalcContext, treatedAsDebt: ReadonlySet<Id>): readonly Decimal[] {
  return buckets.map((bucket) => {
    const lastDay = addDays(bucket.end, -1);
    return add(totalPropertyEquity(plan, lastDay, ctx, treatedAsDebt), totalDepreciatingAssetValue(plan, lastDay));
  });
}

/** [start of today's month, the end of today): the part of this month that is already inside now (R21). */
const soFarWindow = (window: Window, today: Day): Window => ({ start: window.start, end: addDays(today, 1), interval: window.interval });

const zipAmounts = (a: Series, b: Series, f: (x: Decimal, y: Decimal) => Decimal): Series =>
  a.slice(0, Math.min(a.length, b.length)).map((bucket, i) => ({ ...bucket, amount: f(bucket.amount, b[i]!.amount) }));

const roundSeries = (series: Series): Series => series.map((b) => ({ ...b, amount: round2(b.amount) }));

const roundPosition = (p: Position): Position => ({
  cash: round2(p.cash),
  investments: round2(p.investments),
  debt: round2(p.debt),
  propertyEquity: round2(p.propertyEquity),
  assetValue: round2(p.assetValue),
  netWorth: round2(p.netWorth),
});

/**
 * Opportunity cost: each debt then expense as a synthetic investment at the plan's return with
 * no opening balance, match or relief. Later entries win for duplicate ids, as a Swift dictionary.
 */
function potentialInvestments(
  subjects: readonly Transaction[],
  window: Window,
  annualReturnPercent: Decimal,
): ReadonlyMap<Id, InvestmentInterestForecast> {
  return new Map(
    subjects.map((t) => [
      t.id,
      createInvestmentTransactionForecast(
        {
          ...t,
          type: 'investment',
          interestRate: annualReturnPercent,
          initialBalance: ZERO,
          employerMatch: null,
          taxRelief: null,
          paymentDayOfMonth: null,
          linkedAssetId: null,
        },
        window,
      ),
    ] as const),
  );
}

/** ForecastEngine.calculateForecast(for:asOf:startingFrom:mortgageIdsTreatedAsDebt:). */
export function calculateForecast(plan: Plan, ctx: CalcContext, options: ForecastOptions = {}): ForecastResult {
  const config = plan.configuration;
  const window = windowFromPlanConfiguration(config.timeframeYears, ctx.now);
  const sold = planSaleDates(plan);
  const transactions = settleSoldAssetDebts(plan.transactions.filter((t) => t.isActive), sold, { onlyDebts: false });
  const investments = ofType(transactions, 'investment');
  const incomes = ofType(transactions, 'income');

  const cashflowInputs = {
    transactions,
    properties: plan.properties,
    depreciatingAssets: plan.depreciatingAssets,
    assets: plan.assets,
    calculationWindow: window,
    viewWindow: null,
    expectedReturnPercent: config.expectedReturnPercent,
  };
  const cashflow = calculateCashflow(cashflowInputs, ctx);

  const checkpoint = options.checkpoint ?? null;
  const investmentCheckpoint = options.investmentCheckpoint === undefined ? checkpoint : options.investmentCheckpoint;
  const baseline = cashBaseline(plan, checkpoint, window.start);
  const potsAt = (day: Day): Decimal => potTotals(investments, incomes, [day], config.expectedReturnPercent)[0]!;
  const potsNow = potsAt(ctx.now);
  const invested = investmentBaseline(investmentCheckpoint);
  // R18: what the pots do not explain is carried as it is from the check-in, never grown.
  const unexplained = invested === null || invested.since > ctx.now ? ZERO : sub(invested.investments, potsAt(invested.since));
  const fromPlan = currentPosition(
    {
      initialBalance: baseline.cash,
      cashSinceDate: baseline.since,
      investmentBaseline: null,
      transactions: plan.transactions.filter((t) => t.isActive),
      properties: plan.properties,
      assets: activeAssets(plan),
      depreciatingAssets: plan.depreciatingAssets,
      expectedReturnPercent: config.expectedReturnPercent,
    },
    ctx,
  );
  const investmentsNow = add(potsNow, unexplained);
  const now: Position = {
    ...fromPlan,
    investments: investmentsNow,
    netWorth: add(add(sub(add(fromPlan.cash, investmentsNow), fromPlan.debt), fromPlan.propertyEquity), fromPlan.assetValue),
  };

  // R20, R21: this month's bucket ends at now plus what is still to come, so each month opens from now
  // less what this month has already paid.
  const soFar = soFarWindow(window, ctx.now);
  const paidSoFar = calculateCashflow({ ...cashflowInputs, calculationWindow: soFar }, ctx).windowed.disposable[0]?.amount ?? ZERO;
  const openingInvestments = potsAt(addDays(window.start, -1));
  const buckets = timeBuckets(window);
  const potsAtMonthEnds = potTotals(investments, incomes, buckets.map((b) => addDays(b.end, -1)), config.expectedReturnPercent);
  const pots: Series = buckets.map((bucket, i) => ({ ...bucket, amount: potsAtMonthEnds[i]! }));

  const treatedAsDebt: ReadonlySet<Id> = new Set(options.mortgageIdsTreatedAsDebt ?? []);
  const allDebts = ofType(transactions, 'debt');
  const properties = propertyIds(plan);
  const debtLine = allDebts.filter((d) => countsAsDebt(d, properties, treatedAsDebt));
  const settlements = saleSettlements(sold, allDebts);
  const debt = projectDebts(debtLine, window, ctx.now, settlements);

  const openingCash = sub(now.cash, paidSoFar);
  const cash = accumulate(openingCash, cashflow.windowed.disposable);
  const investment = pots.map((bucket) => ({ ...bucket, amount: add(bucket.amount, unexplained) }));
  const equity = withAmounts(cash, equitySeries(plan, cash, ctx, treatedAsDebt));
  const netWorth = cash.map((bucket, i): Bucket => ({
    ...bucket,
    amount: sub(
      add(add(bucket.amount, investment[i]?.amount ?? ZERO), equity[i]!.amount),
      debt[i]?.amount ?? ZERO,
    ),
  }));
  const passive = zipAmounts(cash, investment, (c, inv) =>
    safeWithdrawalMonthlyIncome(add(c, inv), config.safeWithdrawalRatePercent),
  );

  return {
    window,
    cashflow,
    cumulative: {
      cash: roundSeries(cash),
      investment: roundSeries(investment),
      equity: roundSeries(equity),
      debt: roundSeries(debt),
      netWorth: roundSeries(netWorth),
      passive: roundSeries(passive),
      potential: potentialInvestments(
        [...ofType(transactions, 'debt'), ...ofType(transactions, 'expense')],
        window,
        config.expectedReturnPercent,
      ),
      debtFreeDate: debtFreeDate(debtLine, roundSeries(debt), settlements, ctx.now),
    },
    now: roundPosition(now),
    opening: openingValues(plan, window, ctx, {
      cash: openingCash,
      investment: add(openingInvestments, unexplained),
      debtLine,
      settlements,
      treatedAsDebt,
    }),
  };
}

/** The line at the end of the day before the window opens: the previous month's last day. */
function openingValues(
  plan: Plan,
  window: Window,
  ctx: CalcContext,
  known: { readonly cash: Decimal; readonly investment: Decimal; readonly debtLine: readonly Transaction[]; readonly settlements: ReadonlyMap<Id, Day>; readonly treatedAsDebt: ReadonlySet<Id> },
): LineValues {
  const previous: Window = { start: addMonths(window.start, -1), end: window.start, interval: 'month' };
  const debt = projectDebts(known.debtLine, previous, ctx.now, known.settlements)[0]?.amount ?? ZERO;
  const lastDay = addDays(window.start, -1);
  const equity = add(totalPropertyEquity(plan, lastDay, ctx, known.treatedAsDebt), totalDepreciatingAssetValue(plan, lastDay));
  return {
    cash: round2(known.cash),
    investment: round2(known.investment),
    equity: round2(equity),
    debt: round2(debt),
    netWorth: round2(sub(add(add(known.cash, known.investment), equity), debt)),
  };
}
